import { Card, LevelMeter } from '@wundo/ui';
import type { QuestionCategoryCount } from '@/types/dashboard.ts';
import { CATEGORY_META } from './questionCategories.ts';

export interface CategoryCardProps {
  category: QuestionCategoryCount;
  /** The LLM's line about this domain. Null once loaded and still withheld. */
  description: string | null;
  /** The descriptions call is still out and this domain has no answer yet. */
  loading: boolean;
}

/** One leadership domain: how many questions landed in it, and what they were about. */
export function CategoryCard({ category, description, loading }: CategoryCardProps) {
  const { title, Icon } = CATEGORY_META[category.category];

  return (
    <Card as='li' className='flex flex-col gap-3'>
      <div className='flex items-center gap-3'>
        <Icon className='size-4 shrink-0 text-ink-muted' aria-hidden='true' />
        <h3 className='min-w-0 flex-1 truncate text-sm font-semibold text-ink'>
          {title}
        </h3>
        <span className='shrink-0 text-xs font-semibold text-ink-muted tabular-nums'>
          {category.percentage}% · {category.count}
        </span>
      </div>

      <div className='flex-1'>
        {loading && <p className='text-xs text-ink-muted'>Loading insights…</p>}
        {!loading && description !== null && (
          <p className='text-xs leading-relaxed text-ink-muted'>{description}</p>
        )}
        {!loading && description === null && (
          <p className='text-xs leading-relaxed text-ink-muted italic'>
            Fewer than five unique users have raised this so far. To protect
            confidentiality, we&apos;ll expand insights as more input comes in.
          </p>
        )}
      </div>

      <LevelMeter
        value={category.percentage / 100}
        label={`${category.percentage}% of questions`}
        className='h-1'
      />
    </Card>
  );
}
